import React, { useState } from 'react';
import { CheckCircle2Icon, Loader2Icon } from 'lucide-react';
interface InquiryFormProps {
  title: string;
  topicLabel?: string;
  topics?: string[];
  showGroupSize?: boolean;
  submitLabel?: string;
}
type Status = 'idle' | 'submitting' | 'success' | 'error';
type Errors = Partial<Record<'name' | 'email' | 'phone', string>>;
const inputCls = 'mt-1.5 h-12 w-full rounded-[3px] border border-navy/20 bg-white px-3.5 text-[15px] text-ink placeholder:text-steel/70 focus:border-navy focus:outline-none focus:ring-2 focus:ring-navy/15 aria-[invalid=true]:border-accent';
export function InquiryForm({
  title,
  topicLabel = 'Topic',
  topics,
  showGroupSize,
  submitLabel = 'Send Inquiry'
}: InquiryFormProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [errors, setErrors] = useState<Errors>({});
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const next: Errors = {};
    if (!String(data.get('name') ?? '').trim()) next.name = 'Please enter your name.';
    if (!/^\S+@\S+\.\S+$/.test(String(data.get('email') ?? ''))) next.email = 'Please enter a valid email.';
    if (String(data.get('phone') ?? '').replace(/\D/g, '').length < 10) next.phone = 'Please enter a 10-digit phone number.';
    setErrors(next);
    if (Object.keys(next).length) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 900);
  };
  if (status === 'success') {
    return <div className="flex flex-col items-start gap-4 rounded-[4px] bg-white p-8 ring-1 ring-navy/10" role="status" data-id="element-94" data-mp="2jgm8189uhiql">
        <CheckCircle2Icon className="h-10 w-10 text-accent" aria-hidden="true" data-id="element-95" data-mp="2sf7lip0gm7ki" />
        <h3 className="t-h3 text-navy" data-id="element-96" data-mp="2shajbv7ut4zs">Request received</h3>
        <p className="text-steel" data-id="element-97" data-mp="cmibkjkvdihc">Thank you. A member of our team will reach out within one business day. Need an answer sooner? Call (702) 375-5900.</p>
        <button type="button" onClick={() => setStatus('idle')} className="text-sm font-semibold text-navy underline underline-offset-4" data-id="element-98" data-mp="fpojfys1w7nx">
          Send another inquiry
        </button>
      </div>;
  }
  return <form onSubmit={handleSubmit} noValidate className="rounded-[4px] bg-white p-6 ring-1 ring-navy/10 md:p-8" data-id="element-99" data-mp="opkyr9sjvu6b">
      <h3 className="t-h3 text-navy" data-id="element-100" data-mp="to4ntj2fxm2w">{title}</h3>
      {status === 'error' && <p className="mt-3 rounded-[3px] bg-accent/10 px-3 py-2 text-sm text-accent" role="alert" data-id="element-101" data-mp="3s7bjva8cy8ig">
          Please fix the highlighted fields.
        </p>}
      <div className="mt-6 grid gap-5 sm:grid-cols-2" data-id="element-102" data-mp="y5uvcl4l4sy">
        <Field id="name" label="Full name" error={errors.name} data-id="element-103" data-mp="5yk2wumq8eqz">
          <input id="name" name="name" autoComplete="name" className={inputCls} aria-invalid={!!errors.name} aria-describedby={errors.name ? 'name-error' : undefined} data-id="element-104" data-mp="aq1uwt6rv4zt" />
        </Field>
        <Field id="email" label="Email" error={errors.email} data-id="element-105" data-mp="39ehdv5sppqqd">
          <input id="email" name="email" type="email" autoComplete="email" className={inputCls} aria-invalid={!!errors.email} aria-describedby={errors.email ? 'email-error' : undefined} data-id="element-106" data-mp="3edu1vtvbl3uj" />
        </Field>
        <Field id="phone" label="Phone" error={errors.phone} data-id="element-107" data-mp="31xc7zte70p12">
          <input id="phone" name="phone" type="tel" autoComplete="tel" className={inputCls} aria-invalid={!!errors.phone} aria-describedby={errors.phone ? 'phone-error' : undefined} data-id="element-108" data-mp="2wwwxd9lapvms" />
        </Field>
        {topics && <Field id="topic" label={topicLabel} data-id="element-109" data-mp="2nxteo6q1ji8t">
            <select id="topic" name="topic" className={inputCls} defaultValue={topics[0]} data-id="element-110" data-mp="2j6bbrd4gj7l7">
              {topics.map(t => <option key={t} data-id="element-111" data-mp="2e6f8jg3l3k6p">{t}</option>)}
            </select>
          </Field>}
        {showGroupSize && <Field id="size" label="Group size" data-id="element-112" data-mp="299yk0ksh33t3">
            <select id="size" name="size" className={inputCls} defaultValue="2–4" data-id="element-113" data-mp="248sn0ars4udr">
              {['1', '2–4', '5–9', '10–20', '20+'].map(s => <option key={s} data-id="element-114" data-mp="2088tdh6f08wd">{s}</option>)}
            </select>
          </Field>}
        <Field id="dates" label="Preferred dates (optional)" data-id="element-115" data-mp="2oyv6crnkl9lk">
          <input id="dates" name="dates" placeholder="e.g. weekends in November" className={inputCls} data-id="element-116" data-mp="2tw4u82vb9d7a" />
        </Field>
        <div className="sm:col-span-2" data-id="element-117" data-mp="2g59uph6pc8gd">
          <Field id="message" label="Tell us about your goals (optional)" data-id="element-118" data-mp="2k7d84bcxln4a">
            <textarea id="message" name="message" rows={4} className={`${inputCls} h-auto py-3`} data-id="element-119" data-mp="2k5a7kxe5kblb" />
          </Field>
        </div>
      </div>
      <button type="submit" disabled={status === 'submitting'} className="mt-6 inline-flex h-12 items-center justify-center gap-2 rounded-[3px] bg-accent px-8 font-display text-sm font-semibold uppercase tracking-[0.08em] text-white transition-colors duration-150 hover:bg-accent-bright disabled:opacity-70" data-id="element-120" data-mp="2b77z5ws3ago0">
        {status === 'submitting' && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-121" data-mp="3rj3usop4j5ag" />}
        {status === 'submitting' ? 'Sending…' : submitLabel}
      </button>
    </form>;
}
function Field({
  id,
  label,
  error,
  children
}: {
  id: string;
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-122" data-mp="1fvs01n8d598i">
      <label htmlFor={id} className="text-sm font-semibold text-navy" data-id="element-123" data-mp="1tozr9dsj82po">
        {label}
      </label>
      {children}
      {error && <p id={`${id}-error`} className="mt-1 text-sm text-accent" data-id="element-124" data-mp="1ox883obpgz9k">
          {error}
        </p>}
    </div>;
}